// components/TeacherRealWordReadingA.js
import React, { useState, useMemo, useEffect } from "react";

function TeacherRealWordReadingA({
  taskData,
  studentId,
  assessmentInstanceId,
  taskIndex,
  numTasks,
  taskId,
  teacherResponses,
  setTeacherResponses,
}) {
  
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);

  const practiceQuestions = useMemo(
    () => (taskData.questions || []).filter(q => q.practice === true),
    [taskData]
  );

  const testQuestions = useMemo(
    () => (taskData.questions || []).filter(q => !q.practice),
    [taskData]
  );

  // Timer logic
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleTimerStart = () => {
    setTimerRunning(true);
  };

  const handleTimerPause = () => {
    setTimerRunning(false);
  };

  const handleTimerReset = () => {
    if (window.confirm('Reset timer? This will clear the time.')) {
      setTimerRunning(false);
      setTimerSeconds(0);
    }
  };

  const handleResponseChange = (questionId, value) => {
    setTeacherResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateScore = () => {
    let correct = 0;
    testQuestions.forEach(q => {
      if (teacherResponses[q.id] === 'correct') {
        correct++;
      }
    });
    return correct;
  };

  console.log("TeacherRealWordReadingA:", { 
    practiceQuestions, 
    testQuestions, 
    teacherResponses,
    score: calculateScore()
  });

  return (
    <div>
      {/* Timer Section */}
      <div style={{
        border: "1px solid #3498db",
        borderRadius: "8px",
        padding: "20px",
        marginBottom: "20px",
        textAlign: "center",
        background: "#f8f9fa"
      }}>
        <div style={{ marginBottom: "12px", fontWeight: "600" }}>Timer</div>
        <div style={{ 
          fontSize: "2rem", 
          fontWeight: "700", 
          marginBottom: "16px",
          fontFamily: "monospace"
        }}>
          {formatTime(timerSeconds)}
        </div>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
          <button
            onClick={handleTimerStart}
            disabled={timerRunning}
            style={{
              background: "#27AE60",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "8px 20px",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: timerRunning ? "not-allowed" : "pointer",
              opacity: timerRunning ? 0.5 : 1
            }}
          >
            Start Timer & Recording
          </button>
          <button
            onClick={handleTimerPause}
            disabled={!timerRunning}
            style={{
              background: "#f39c12",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "8px 20px",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: !timerRunning ? "not-allowed" : "pointer",
              opacity: !timerRunning ? 0.5 : 1
            }}
          >
            Pause
          </button>
          <button
            onClick={handleTimerReset}
            style={{
              background: "#e74c3c",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "8px 20px",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer"
            }}
          >
            Reset
          </button>
        </div>
        <div style={{ 
          marginTop: "12px", 
          fontSize: "0.75rem", 
          color: "#666",
          fontStyle: "italic"
        }}>
          Note: Timer auto-populates into "Time" field. Reset triggers confirmation modal.
        </div>
      </div>

      {/* Instructions */}
      <div style={{
        background: "#fff9e6",
        border: "1px solid #f1c40f",
        borderRadius: "6px",
        padding: "16px",
        marginBottom: "20px"
      }}>
        <div style={{ fontWeight: "700", marginBottom: "8px" }}>
          Instructions/Directions:
        </div>
        <div style={{ fontSize: "0.9rem", color: "#555", lineHeight: "1.6" }}>
          Say "Let's read some words, you are very good at reading! I will show you some flash cards with words on them. If there is a word you cannot read, just try it. I want you to read them as quickly as you can. First let's try a couple for practice. If the child gets the practice words incorrect, correct them by saying '/p/ /a/ /n/ is 'pan'."
        </div>
      </div>

      {/* Practice Items */}
      <div style={{
        background: "#f0f0f0",
        padding: "16px",
        borderRadius: "6px",
        marginBottom: "20px"
      }}>
        <div style={{ 
          fontWeight: "700", 
          marginBottom: "16px",
          fontSize: "0.95rem"
        }}>
          Practice Items (Feedback Allowed)
        </div>

        {practiceQuestions.map((q, idx) => {
          const word = q.answers?.[0]?.text || "";
          const correctCount = practiceQuestions.filter(pq => 
            teacherResponses[pq.id] === 'correct'
          ).length;
          const incorrectCount = practiceQuestions.filter(pq => 
            teacherResponses[pq.id] === 'incorrect'
          ).length;

          return (
            <div 
              key={q.id}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: idx < practiceQuestions.length - 1 ? "1px solid #ddd" : "none"
              }}
            >
              <div style={{ 
                minWidth: "80px", 
                fontWeight: "600",
                fontSize: "1rem"
              }}>
                {word}
              </div>
              <div style={{ 
                display: "flex", 
                gap: "24px",
                marginLeft: "auto"
              }}>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "0.9rem"
                }}>
                  <input
                    type="radio"
                    name={`practice-${q.id}`}
                    value="correct"
                    checked={teacherResponses[q.id] === 'correct'}
                    onChange={(e) => handleResponseChange(q.id, e.target.value)}
                  />
                  ✓ Correct ({correctCount})
                </label>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "0.9rem"
                }}>
                  <input
                    type="radio"
                    name={`practice-${q.id}`}
                    value="incorrect"
                    checked={teacherResponses[q.id] === 'incorrect'}
                    onChange={(e) => handleResponseChange(q.id, e.target.value)}
                  />
                  ✗ Incorrect ({incorrectCount})
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stop Feedback Divider */}
      <div style={{
        border: "2px dashed #999",
        borderRadius: "6px",
        padding: "16px",
        textAlign: "center",
        marginBottom: "20px",
        fontSize: "1.1rem",
        fontWeight: "700",
        color: "#666",
        background: "#fafafa"
      }}>
        STOP FEEDBACK
      </div>

      {/* Second Instructions */}
      <div style={{
        background: "#fff9e6",
        border: "1px solid #f1c40f",
        borderRadius: "6px",
        padding: "16px",
        marginBottom: "20px"
      }}>
        <div style={{ fontWeight: "700", marginBottom: "8px" }}>
          Instructions/Directions:
        </div>
        <div style={{ fontSize: "0.9rem", color: "#555", lineHeight: "1.6" }}>
          Say "Well done. Now let's do some more. Just try your best and remember read the word as quickly as you can. Start recording and stop watch now!"
        </div>
      </div>

      {/* Test Items */}
      <div style={{
        background: "#f9f9f9",
        padding: "16px",
        borderRadius: "6px",
        marginBottom: "20px"
      }}>
        <div style={{ 
          fontWeight: "700", 
          marginBottom: "16px",
          fontSize: "0.95rem"
        }}>
          Test Items ({testQuestions.length} words - No Feedback)
        </div>

        {testQuestions.map((q, idx) => {
          const word = q.answers?.[0]?.text || "";
          const correctCount = testQuestions.filter(tq => 
            teacherResponses[tq.id] === 'correct'
          ).length;
          const incorrectCount = testQuestions.filter(tq => 
            teacherResponses[tq.id] === 'incorrect'
          ).length;

          return (
            <div 
              key={q.id}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 0",
                borderBottom: idx < testQuestions.length - 1 ? "1px solid #ddd" : "none"
              }}
            >
              <div style={{ 
                minWidth: "80px", 
                fontWeight: "600",
                fontSize: "1rem"
              }}>
                {word}
              </div>
              <div style={{ 
                display: "flex", 
                gap: "24px",
                marginLeft: "auto"
              }}>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "0.9rem"
                }}>
                  <input
                    type="radio"
                    name={`test-${q.id}`}
                    value="correct"
                    checked={teacherResponses[q.id] === 'correct'}
                    onChange={(e) => handleResponseChange(q.id, e.target.value)}
                  />
                  ✓ Correct ({correctCount})
                </label>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "0.9rem"
                }}>
                  <input
                    type="radio"
                    name={`test-${q.id}`}
                    value="incorrect"
                    checked={teacherResponses[q.id] === 'incorrect'}
                    onChange={(e) => handleResponseChange(q.id, e.target.value)}
                  />
                  ✗ Incorrect ({incorrectCount})
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* Score Display */}
      <div style={{
        background: "#d4edda",
        border: "2px solid #28a745",
        borderRadius: "6px",
        padding: "16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px"
      }}>
        <div style={{ fontWeight: "700", fontSize: "1.1rem" }}>
          Total Score: {calculateScore()} / {testQuestions.length}
          <span style={{ fontSize: "0.85rem", fontWeight: "400", marginLeft: "8px" }}>
            (auto-tallied)
          </span>
        </div>
        <div style={{ fontWeight: "700", fontSize: "1.1rem" }}>
          Time: {formatTime(timerSeconds)}
          <span style={{ fontSize: "0.85rem", fontWeight: "400", marginLeft: "8px" }}>
            (auto-populated from timer)
          </span>
        </div>
      </div>
    </div>
  );
}

export default TeacherRealWordReadingA;